import { DateTime } from 'luxon'
import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faCalendarCheck } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import type { UseFiltersPayload } from '@domain-group/react-hooks'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PageContext from '@contexts/pageContext'

import useAgencySelector from '@hooks/useAgencySelector'
import { generateUrls } from '@hooks/useCampaignInspection'
import useCampaignInspections from '@hooks/useCampaignInspections'
import useCurrentUser from '@hooks/useCurrentUser'

import { groupInspectionsByDate } from '@functions/campaignInspection'

import defaultRequestOptions from '@sections/Client/packs/Overviews/defaultRequestOptions'

import useSelector from '@hooks/useSelector'
import CampaignInspectionsBlankState from '../CampaignInspectionsBlankState'
import CampaignInspectionsDateGroup from '../CampaignInspectionsDateGroup'

const buildPageFilterFields = () => {
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'campaigns',
      },
      key: 'campaign_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'users',
        entityTitleKey: 'full_name',
        label: 'Agent',
        searchRequestOptions: {
          actable_types: ['Agent'],
        },
      },
      key: 'userId',
    },
  ]
}

interface PageContentProps {
  filterPayload: UseFiltersPayload,
}

const PageContent = (props: PageContentProps) => {
  const { filterPayload } = props
  const { pageFilters, persistedFilters } = filterPayload

  // @ts-expect-error TS2339
  const { userId } = pageFilters

  const { agencyIdParam, selectedAgencyId } = useAgencySelector()
  const { isAgent, isEbuAdmin } = useCurrentUser()

  const users = useSelector(reduxState => reduxState.entities.users)

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters, updateFilters,
    },
    defaultFilters,
  } = pageContext

  const campaignInspectionsPayload = useCampaignInspections({
    filters: {
      agency_id: isAgent || isEbuAdmin ? undefined : selectedAgencyId,
      agent_id: userId ? users[userId]?.actable_id : undefined,
      inspection_date_start: DateTime.now().toFormat('yyyy-MM-dd'),
      ...pageFilters,
    },
    performHttpRequests: !!agencyIdParam,
    requestOptions: {
      ...defaultRequestOptions.campaignInspection,
    },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignInspections,
    filteredCampaignInspectionsCount,
    hasFilteredCampaignInspections,
    loading,
  } = campaignInspectionsPayload

  const {
    campaignInspectionsOverviewUrl,
  } = generateUrls({}, { prefixRoute: `#/agency/dashboard/agencies/${agencyIdParam}/overview` })

  const groupedCampaignInspections = groupInspectionsByDate(filteredCampaignInspections)

  return (
    <>
      <Helmet>
        <title>Inspections | Realhub</title>
      </Helmet>

      <PageHeader
        nestedNavigation
        activeTabBarItemKey="inspections"
        tabBarItems={[
          {
            href: campaignInspectionsOverviewUrl,
            icon: faCalendarCheck,
            key: 'inspections',
            title: 'Overview',
          },
        ]}
        title="Inspections"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation
              locationKey="agency.agencyDashboard.inspections.banner"
            />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredCampaignInspections && <CampaignInspectionsBlankState />}

            {!loading && hasFilteredCampaignInspections && (
              <>
                {Object.entries(groupedCampaignInspections).map(([dateString, { campaignInspections }]) => (
                  <CampaignInspectionsDateGroup dateString={dateString} campaignInspections={campaignInspections} />
                ))}

                <Box marginBottom="large" variant="white">
                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredCampaignInspectionsCount}
                    loading={loading}
                    showBorderTop={false}
                  />
                </Box>
              </>
            )}

          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation
              locationKey="agency.agencyDashboard.inspections.sidebar"
            />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              externalFilters={persistedFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
